import { useState, useEffect, useMemo } from 'react';
import DataTable, { Rozet } from '../../components/DataTable';
import { apiRequest } from '../../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DatePicker from '../../components/DatePicker';
import UsageInfo from '../../components/UsageInfo';
import Pagination from '../../components/Pagination';

const bugunDate = new Date();
const pad = (n) => String(n).padStart(2, '0');
const bugunFmt = [pad(bugunDate.getDate()), pad(bugunDate.getMonth() + 1), bugunDate.getFullYear()].join('.');

const formatTarih = (isoStr) => {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const parseDate = (str) => {
  if (!str) return null;
  const [g, a, y] = str.split('.').map(Number);
  return new Date(y, a - 1, g);
};

const addDays = (str, n) => {
  const [g, a, y] = str.split('.').map(Number);
  const d = new Date(y, a - 1, g + n);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};


export default function KBSIslemleri() {
  const [rezervasyonlar, setRezervasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tarih, setTarih] = useState(bugunFmt);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('ALL'); // ALL, ENTRY, EXIT
  const [aktifSayfa, setAktifSayfa] = useState(1);
  const [sayfaBasi, setSayfaBasi] = useState(100);

  const veriYukle = async () => {
    setLoading(true);
    try {
      const data = await apiRequest('rezervasyonlar');
      setRezervasyonlar(data || []);
    } catch (err) {
      console.error('KBS Veri Hatası:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    veriYukle();
  }, []);

  // ── Flatten & Filter Data ──
  const kbsHamListesi = useMemo(() => {
    const flattened = [];
    const selDate = parseDate(tarih);
    if (!selDate) return [];
    
    // Normalize date for strict string comparison
    const selDateStr = Array.from([selDate.getFullYear(), pad(selDate.getMonth()+1), pad(selDate.getDate())]).join('-');

    rezervasyonlar.forEach(rez => {
      (rez.rezervasyonMisafirler || []).forEach(rm => {
        const m = rm.misafir || {};
        const girisDateObj = new Date(rez.girisTarihi);
        const girisStr = Array.from([girisDateObj.getFullYear(), pad(girisDateObj.getMonth()+1), pad(girisDateObj.getDate())]).join('-');
        
        let cikisStr = '';
        if (rez.cikisTarihi) {
            const cikisDateObj = new Date(rez.cikisTarihi);
            cikisStr = Array.from([cikisDateObj.getFullYear(), pad(cikisDateObj.getMonth()+1), pad(cikisDateObj.getDate())]).join('-');
        }
        
        // Tarih sınırlarında mı kontrol et (Olay günü otelde miydi?)
        const isGiris = (girisStr === selDateStr);
        const isCikis = (cikisStr === selDateStr);
        const isOnTarihteOtelde = (rez.durum !== 'İptal' && rez.durum !== 'No Show' && rez.durum !== 'C-OUT Yapıldı') &&
                                  (selDateStr >= girisStr) &&
                                  (!cikisStr || selDateStr <= cikisStr);

        const item = {
          id: `${rez.rezervasyonId}-${m.misafirId}`,
          rezId: rez.rezervasyonId,
          adSoyad: m.adSoyad || 'İsimsiz Misafir',
          tc: m.tcPasaportNo || m.pasaportNo || '—',
          oda: rez.odaNo || '?',
          giris: rez.girisTarihi,
          cikis: rez.cikisTarihi,
          durum: rez.durum,
          uyruk: (() => { const u = m.uyruk || m.ulke || ''; if (!u) return 'TÜRKİYE'; const ul = u.toLocaleLowerCase('tr-TR'); if (ul === 'türk' || ul === 'tc' || ul === 'türkiye') return 'TÜRKİYE'; return u; })(),
          cinsiyet: m.cinsiyet || 'Belirsiz',
          dogumTarihi: m.dogumTarihi,
          dogumYeri: m.dogumYeri || '',
          anaAdi: m.anaAdi || '',
          babaAdi: m.babaAdi || '',
          aracPlaka: m.aracPlaka || '',
          isGiris: isGiris,
          isCikis: isCikis,
          isOnTarihteOtelde: isOnTarihteOtelde
        };

        // Eğer o gün otelle bir alakası varsa ham listeye ekle
        if (item.isGiris || item.isCikis || item.isOnTarihteOtelde) {
          flattened.push(item);
        }
      });
    });

    return flattened;
  }, [rezervasyonlar, tarih]);

  // Tablo filtreleri uygulanmış versiyon
  const kbsListesi = useMemo(() => {
    return kbsHamListesi.filter(item => {
      let show = false;
      if (filterType === 'ALL') {
        show = item.isGiris || item.isCikis || item.isOnTarihteOtelde;
      }
      else if (filterType === 'ENTRY') show = item.isGiris;
      else if (filterType === 'EXIT') show = item.isCikis;
      else if (filterType === 'STAYING') show = item.isOnTarihteOtelde && !item.isGiris && !item.isCikis;
      else if (filterType === 'FOREIGN') show = (item.isGiris || item.isCikis || item.isOnTarihteOtelde) && (item.uyruk !== 'TÜRKİYE' && item.uyruk !== 'TC');

      if (show && search) {
        const s = search.toLocaleLowerCase('tr-TR');
        const match =
          item.adSoyad.toLocaleLowerCase('tr-TR').includes(s) ||
          item.tc.includes(search) ||
          String(item.oda).includes(search);
        if (!match) show = false;
      }
      return show;
    });
  }, [kbsHamListesi, filterType, search]);

  useEffect(() => { setAktifSayfa(1); }, [filterType, search, tarih]);

  const stats = useMemo(() => {
    return {
      entries: kbsHamListesi.filter(i => i.isGiris).length,
      exits: kbsHamListesi.filter(i => i.isCikis).length,
      staying: kbsHamListesi.filter(i => i.isOnTarihteOtelde && !i.isGiris && !i.isCikis).length,
      foreigners: kbsHamListesi.filter(i => i.uyruk !== 'TÜRKİYE' && i.uyruk !== 'TC').length
    };
  }, [kbsHamListesi]);

  const handleEgmExcelExport = async () => {
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'HotelFlow';
    workbook.lastModifiedBy = 'HotelFlow';
    workbook.created = new Date();
    workbook.modified = new Date();

    // ── Sheet 1: Konaklayanlar ──
    const ws1 = workbook.addWorksheet('Konaklayanlar', { properties: { tabColor: { argb: 'FF00B050' } } });
    
    // Sütun tanımları ve genişlikleri
    ws1.columns = [
      { header: 'Tc Kimlik / Pasaport No', key: 'tc', width: 25 },
      { header: 'Adı', key: 'adi', width: 20 },
      { header: 'Soyadı', key: 'soyadi', width: 20 },
      { header: 'Cinsiyet', key: 'cinsiyet', width: 12 },
      { header: 'Ana Adı', key: 'anaAdi', width: 20 },
      { header: 'Baba Adı', key: 'babaAdi', width: 20 },
      { header: 'Dogum Yeri', key: 'dogumYeri', width: 20 },
      { header: 'Dogum Tarihi', key: 'dogumTarihi', width: 15 },
      { header: 'Arac Plaka', key: 'plaka', width: 15 },
      { header: 'VerilenOda', key: 'oda', width: 15 },
      { header: 'Uyruğu', key: 'uyruk', width: 15 },
      { header: 'Kayıt Durumu', key: 'durum', width: 15 }
    ];

    // Başlık stilini ayarla
    ws1.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FF000000' }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF008000' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });
    ws1.getRow(1).height = 30;

    // Verileri ekle
    kbsHamListesi.forEach((i, idx) => {
      const isimParca = i.adSoyad ? i.adSoyad.split(' ') : [];
      const soyadi = isimParca.length > 1 ? isimParca.pop() : '';
      const adi = isimParca.join(' ') || i.adSoyad || 'Bilinmiyor';
      
      const tc = (i.tc && i.tc !== '—') ? i.tc : '';
      const cinsiyet = i.cinsiyet === 'Erkek' ? 'Erkek' : (i.cinsiyet === 'Kadın' ? 'Kadin' : '');
      const dTarih = formatTarih(i.dogumTarihi);

      const row = ws1.addRow({
        tc: tc,
        adi: adi,
        soyadi: soyadi,
        cinsiyet: cinsiyet,
        anaAdi: i.anaAdi || '',
        babaAdi: i.babaAdi || '',
        dogumYeri: i.dogumYeri || '',
        dogumTarihi: dTarih === '—' ? '' : dTarih,
        plaka: i.aracPlaka || '',
        oda: i.oda || '',
        uyruk: i.uyruk || '',
        durum: ''
      });

      // Hücre kenarlıkları ve hizalama
      row.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' }
        };
      });
    });

    // ── Sheet 2: Ülkeler ──
    const ws2 = workbook.addWorksheet('Ülkeler', { properties: { tabColor: { argb: 'FFF2F2F2' } } });
    ws2.columns = [
      { header: 'ULKE KODU', key: 'kodu', width: 15 },
      { header: 'ULKE ADI', key: 'adi', width: 30 }
    ];

    ws2.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF808080' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    const ulkeler = [
      ["TR", "TÜRKİYE"], ["DE", "ALMANYA"], ["GB", "İNGİLTERE"], ["RU", "RUSYA"],
      ["FR", "FRANSA"], ["US", "ABD"], ["NL", "HOLLANDA"], ["BE", "BELÇİKA"],
      ["CH", "İSVİÇRE"], ["IT", "İTALYA"], ["ES", "İSPANYA"], ["JP", "JAPONYA"],
      ["CN", "ÇİN"], ["UA", "UKRAYNA"]
    ];

    ulkeler.forEach(u => {
      const row = ws2.addRow({ kodu: u[0], adi: u[1] });
      row.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
    });

    // Dosyayı oluştur ve indir
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `EGM_Konaklayan_Ekle_${tarih}.xlsx`);
  };

  const handleExportCsv = () => {
    const headers = ['TC/Pasaport', 'Ad Soyad', 'Oda', 'Giriş Tarihi', 'Çıkış Tarihi', 'Uyruk', 'Cinsiyet'];
    const rows = kbsListesi.map(i => [
      i.tc, i.adSoyad, i.oda, formatTarih(i.giris), formatTarih(i.cikis), i.uyruk, i.cinsiyet
    ]);
    
    let csvContent = "\uFEFF" + headers.join(';') + "\n" + rows.map(e => e.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `KBS_Raporu_${tarih}.csv`);
    link.click();
  };

  return (
    <div className="p-4 sm:p-8 space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-800 tracking-tight">KBS / Jandarma Listesi</h1>
          <p className="text-slate-500 font-bold mt-1 uppercase text-[10px] tracking-[0.2em]">Kimlik Bildirim Sistemi Operasyon Paneli</p>
        </div>
        
        <div className="flex items-center gap-2">
          <button onClick={() => setTarih(t => addDays(t,-1))}
            className="w-11 h-11 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 flex items-center justify-center transition-all shadow-sm font-black">
            ‹
          </button>
          <div className="w-40">
            <DatePicker value={tarih} onChange={setTarih}
              className="w-full text-center font-black text-slate-700 bg-white border-2 border-slate-100 focus:border-indigo-400 rounded-2xl px-4 py-2 text-xs uppercase tracking-widest outline-none transition-all" />
          </div>
          <button onClick={() => setTarih(t => addDays(t,1))}
            className="w-11 h-11 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 flex items-center justify-center transition-all shadow-sm font-black">
            ›
          </button>
          <button onClick={() => setTarih(bugunFmt)}
            className="h-11 px-4 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 text-[10px] font-black uppercase tracking-widest transition-all hidden sm:flex items-center">
            Bugün
          </button>
          <button onClick={veriYukle} 
            className="w-11 h-11 rounded-2xl border-2 border-slate-100 bg-white hover:border-indigo-400 text-slate-500 flex items-center justify-center transition-all shadow-sm text-lg"
          >
            ↺
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'BİLDİRİLECEK GİRİŞ', v: stats.entries, c: 'from-emerald-500 to-teal-600', i: '📥', type: 'ENTRY' },
          { label: 'BİLDİRİLECEK ÇIKIŞ', v: stats.exits, c: 'from-rose-500 to-pink-600', i: '📤', type: 'EXIT' },
          { label: 'KONAKLAYAN', v: stats.staying, c: 'from-blue-500 to-indigo-600', i: '🛏', type: 'STAYING' },
          { label: 'YABANCI MİSAFİR', v: stats.foreigners, c: 'from-amber-400 to-orange-500', i: '🌐', type: 'FOREIGN' }
        ].map(s => (
          <button 
            key={s.label}
            onClick={() => setFilterType(s.type === filterType ? 'ALL' : s.type)}
            className={`bg-gradient-to-br ${s.c} p-6 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group transition-all active:scale-95 ${filterType === s.type ? 'ring-4 ring-offset-4 ring-slate-200 scale-105' : 'opacity-90 hover:opacity-100'}`}
          >
            <span className="absolute top-4 right-4 text-4xl opacity-20 group-hover:scale-125 transition-transform">{s.i}</span>
            <p className="text-[10px] font-black tracking-widest uppercase opacity-70 mb-1">{s.label}</p>
            <h3 className="text-4xl font-black tracking-tighter">{s.v}</h3>
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-[3rem] shadow-2xl border border-slate-100 p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div className="flex-1 relative">
            <input 
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="MİSAFİR ADI, TC VEYA ODA NO İLE ARA..."
              className="w-full bg-slate-50 border-2 border-transparent focus:border-indigo-400 rounded-2xl px-12 py-4 text-xs font-black text-slate-700 outline-none uppercase tracking-widest transition-all"
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl opacity-30">🔍</span>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={handleEgmExcelExport}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-teal-500/20 transition-all active:scale-95"
            >
              AKBS EXCEL İNDİR
            </button>
            <button 
              onClick={handleExportCsv}
              className="bg-slate-900 hover:bg-black text-white px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl transition-all active:scale-95"
            >
              CSV AKTAR
            </button>
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar">
          <DataTable
            anahtarAlan="id"
            satirlar={loading ? [] : kbsListesi.slice((aktifSayfa - 1) * sayfaBasi, aktifSayfa * sayfaBasi)}
            bosIkon="📂"
            bosMetin={loading ? 'Veriler getiriliyor…' : 'Bu tarih için kayıt bulunamadı'}
            kolonlar={[
              {
                anahtar: 'tc', baslik: 'Kimlik / TC No', siralanir: true, filtrelenir: true,
                ciz: k => <span className="font-mono text-xs font-black text-slate-400">{k.tc}</span>,
              },
              {
                anahtar: 'adSoyad', baslik: 'Misafir Ad Soyad', siralanir: true, filtrelenir: true,
                ciz: k => <span className="font-black text-slate-700 text-xs uppercase">{k.adSoyad}</span>,
              },
              {
                anahtar: 'oda', baslik: 'Oda', hizala: 'orta', siralanir: true, filtrelenir: true,
                ciz: k => <Rozet renk="indigo">{k.oda}</Rozet>,
              },
              {
                anahtar: 'giris', baslik: 'Giriş / Çıkış', siralanir: true,
                ciz: k => (
                  <div className="flex flex-col gap-1">
                    <span className={'text-[9px] font-black ' + (k.isGiris ? 'text-emerald-600' : 'text-slate-400')}>
                      G: {formatTarih(k.giris)}
                    </span>
                    <span className={'text-[9px] font-black ' + (k.isCikis ? 'text-rose-600' : 'text-slate-400')}>
                      Ç: {formatTarih(k.cikis)}
                    </span>
                  </div>
                ),
              },
              {
                anahtar: 'uyruk', baslik: 'Uyruk', hizala: 'orta', siralanir: true, filtrelenir: true,
                ciz: k => <Rozet renk={k.uyruk === 'TÜRKİYE' ? 'gray' : 'amber'}>{k.uyruk}</Rozet>,
              },
              {
                anahtar: 'durum', baslik: 'Durum', hizala: 'orta', siralanir: true, filtrelenir: true,
                ciz: k => (
                  <Rozet renk={k.durum?.includes('C-IN') ? 'emerald' : k.durum?.includes('C-OUT') ? 'rose' : 'amber'}>
                    {k.durum}
                  </Rozet>
                ),
              },
            ]}
          />
        </div>
        <Pagination toplam={kbsListesi.length} sayfaBasi={sayfaBasi} sayfaBasiDegistir={setSayfaBasi} aktifSayfa={aktifSayfa} sayfaDegistir={setAktifSayfa} />
      </div>

      <UsageInfo
        id="kbs-usage"
        color="indigo"
        title="Nasıl Kullanılır?"
        description="KBS / AKBS (Kimlik Bildirim Sistemi) sayfasında otelinizin Emniyet ile olan günlük konaklama bildirim süreçlerini zahmetsizce yönetebilirsiniz."
        scenarios={[
          { title: "Giriş ve Çıkış İşlemleri", content: "O gün otele giriş yapacakları ve otelden ayrılacakları yukarıdaki renkli kartlara tıklayarak filtreleyebilir, eksik kimlik bilgilerini hızlıca gözlemleyebilirsiniz." },
          { title: "İçeride Konaklayanlar & Yabancılar", content: "Mavi 'Konaklayan' kartına tıklayarak o an itibariyle otel odalarınızda uyuyan herkesin yasal listesini, turuncu karta basarak yabancı uyrukluları görebilirsiniz." },
          { title: "Otomatik Emniyet Bildirimi (AKBS)", content: "Tüm kontrolleriniz bittiğinde yeşil renkli 'AKBS XML İNDİR' butonuna tıklayarak resmi Emniyet (AKBS) formatındaki XML dosyasını anında alabilir ve Emniyet sistemine tek tıkla yükleyebilirsiniz." },
          { title: "CSV Raporlama", content: "Bunun dışında arşivlemek veya kendi excel raporlarınıza eklemek için 'CSV Aktar' butonunu kullanabilirsiniz." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
      `}</style>
    </div>
  );
}